import { Formik } from "formik";
import React from "react";
import { Form } from "react-bootstrap";
import { Button } from '@blueprint/ui';
import styled from "styled-components";
import * as yup from "yup";

import { AuthCodeField, FormFooter } from "../../../common/components/form";
import { FormSubmissionHandler } from "../../../types/common";
import { PageTitle } from "../../../types/pageTitle";

import PublicFormContainer from "./PublicFormContainer";

export type SetupOtpFormValues = {
    otp: string;
};

const schema: yup.SchemaOf<SetupOtpFormValues> = yup.object().shape({
    otp: yup
        .string()
        .required()
        .matches(
            /^[0-9]{6}$/,
            "You didn't enter the expected verification code. Please try again.",
        ),
});

type SetupOtpFormProps = {
    secret: string;
    qrCode: string;
    onSubmit: FormSubmissionHandler<SetupOtpFormValues>;
    onBack: () => void;
};

const SetupOtpForm = ({
    secret,
    qrCode,
    onSubmit,
    onBack,
}: SetupOtpFormProps): JSX.Element => {
    document.title = PageTitle.SetupOtpForm;
    return (
        <StyledPublicFormContainer>
            <Formik
                validationSchema={schema}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={onSubmit}
                initialValues={{
                    otp: "",
                }}
            >
                {({ handleSubmit, isSubmitting }) => (
                    <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                        <h2 className="mb-4">
                            2 Factor Authentication Set Up
                        </h2>
                        <div className="d-flex mb-5">
                            <StyledImg src={qrCode} alt="OTP Key" />
                            <ol className="mb-0 ps-3" id="optSetupPolicy">
                                <li className="mb-3">
                                    Download and install Microsoft Authenticator
                                    app from either Apple Store or Google Play
                                    Store
                                </li>
                                <li className="mb-3">
                                    Scan the QR code or enter the key{" "}
                                    <span className="text-primary fw-bold user-select-all text-break">
                                        {secret}
                                    </span>{" "}
                                    on your app
                                </li>
                                <li>
                                    Enter the authentication code from your
                                    authenticator app below
                                </li>
                            </ol>
                        </div>
                        <AuthCodeField
                            name="otp"
                            label="Authentication Code"
                            autoFocus
                            ariaLabelledBy="optSetupPolicy"
                            required={true}
                        />
                        <FormFooter className="d-flex justify-content-between mb-5">
                            <Button
                                disabled={isSubmitting}
                                onClick={onBack}
                                variant="light.tertiary">Back</Button>
                            <Button
                                type="submit"
                                variant="light.primary"
                                style={{marginLeft: '10px'}}
                                disabled={isSubmitting}>Verify</Button>
                        </FormFooter>
                    </Form>
                )}
            </Formik>
        </StyledPublicFormContainer>
    );
};

const StyledImg = styled.img`
    max-height: 150px;
    max-width: 150px;
`;

const StyledPublicFormContainer = styled(PublicFormContainer)`
    width: 550px;
`;

export default SetupOtpForm;
